import React, { Dispatch } from 'react';
import './Header.scss';
import { ReactComponent as LogoMark } from 'assets/icons/redesign/logo-mark.svg';
import { ReactComponent as ExitIcon } from 'assets/icons/mingcute_exit-line.svg';
import { ReactComponent as SSLogo } from 'assets/icons/ss.svg';
import { Link, useLocation } from 'react-router-dom';
import Button from 'components/ui/Button';
import LoginModal from 'forms/modals/LoginModal';
import { logOut } from 'store/reducers/user';
import { useAppDispatch } from 'store/store';
import GlassButton from 'components/redesign/GlassButton';
import getApiClient from 'js/apiClient';

interface HeaderProps {
    username: string | undefined;
    dispatch: ReturnType<typeof useAppDispatch>;
    form: React.ReactElement;
    modalOpen: boolean;
    header: React.ReactNode | React.ReactNode[];
    subHeader: React.ReactNode | React.ReactNode[];
    setModalOpen: Dispatch<React.SetStateAction<boolean>>;
}
function Header({
    username,
    dispatch,
    form,
    modalOpen,
    setModalOpen,
    header,
    subHeader,
}: HeaderProps): React.ReactElement {
    const { pathname } = useLocation();
    const isAdmin = pathname.indexOf('/admin') === 0;
    const [registrationOpen, setRegistrationOpen] = React.useState(false);

    React.useEffect(() => {
        if (isAdmin) return undefined;
        let active = true;
        getApiClient()
            .get('elections/registration_open/')
            .then(response => {
                if (active) setRegistrationOpen(response.data.open);
            })
            .catch(() => {
                if (active) setRegistrationOpen(false);
            });
        return () => {
            active = false;
        };
    }, [isAdmin]);

    React.useEffect(() => {
        if (isAdmin) return undefined;
        document.body.classList.add('rd');
        return () => document.body.classList.remove('rd');
    }, [isAdmin]);

    const modal = (
        <LoginModal
            open={modalOpen}
            header={header}
            subHeader={subHeader}
            onCancel={() => setModalOpen(false)}
            form={form}
        />
    );

    if (isAdmin) {
        return (
            <header className="legacy-header">
                <div className="logo">
                    <Link to="/">
                        <SSLogo />
                    </Link>
                </div>
                <div className="profile flex-center">
                    {!username && (
                        <Button
                            size="s"
                            color="black"
                            onClick={() => setModalOpen(true)}
                        >
                            Войти
                        </Button>
                    )}
                    {username && (
                        <>
                            <p>Здравствуйте, {username}</p>
                            <Button
                                size="s"
                                color="transparent"
                                onClick={() => dispatch(logOut())}
                            >
                                Выйти
                            </Button>
                        </>
                    )}
                </div>
                {modal}
            </header>
        );
    }

    return (
        <header className="rd-header">
            <div className="rd-container rd-header__nav">
                <Link className="rd-header__logo" to="/">
                    <span className="rd-header__mark">
                        <LogoMark />
                    </span>
                    <span className="rd-header__name">
                        Студенческие
                        <br />
                        онлайн выборы СПбГУТ
                    </span>
                </Link>
                <div className="rd-header__actions">
                    {registrationOpen && (
                        <GlassButton tone="plain" to="/candidates">
                            Анкета для кандидатов
                        </GlassButton>
                    )}
                    {username ? (
                        <GlassButton
                            tone="plain"
                            onClick={() => dispatch(logOut())}
                        >
                            <span className="rd-header__label-full">
                                {username}
                            </span>
                            <span className="rd-header__label-short">
                                {username.split(' ')[0]}
                            </span>
                            <span className="rd-btn__icon rd-header__exit">
                                <ExitIcon />
                            </span>
                        </GlassButton>
                    ) : (
                        <GlassButton
                            tone="dark"
                            onClick={() => setModalOpen(true)}
                        >
                            <span className="rd-header__label-full">
                                Войти в систему
                            </span>
                            <span className="rd-header__label-short">
                                Войти
                            </span>
                        </GlassButton>
                    )}
                </div>
            </div>
            {modal}
        </header>
    );
}

export default React.memo(Header);
